import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
	DataGrid,
	GridRowParams,
	GridRowSelectionModel,
	GridRowId,
} from '@innovyze/stylovyze';
import {
	setAttributeDataGrid,
	setUniqueSelectedIds,
	setUniqueSelectedStatus,
} from '@Actions';
import { tableColumns } from './TabelColumns';
import { TableToolbar } from './TableToolbar';
import {
	AttributeDataGridRow,
	AttributeOption,
	CountReply,
} from '@Types/index';
import {
	selectAttributeDataGrid,
	selectIsCountLoading,
} from '@Selectors/Risk.selectors';
import {
	selectSelectedStatus,
	selectUniqueSelectedIds,
} from '@Selectors/util.selectors';

export const updateUniqueAttributeCounts = (
	scoreRows: AttributeDataGridRow[],
	counts: CountReply[] | undefined,
	isMetric: boolean,
): AttributeDataGridRow[] => {
	if (counts === undefined || counts.length == 0) {
		const newScoreRows: AttributeDataGridRow[] = [];
		for (let i = 0; i < scoreRows.length; i++) {
			newScoreRows.push({
				...scoreRows[i],
				count: undefined,
				length: undefined,
				countPercent: undefined,
				lengthPercent: undefined,
			});
		}
		return newScoreRows;
	} else {
		const conversion = isMetric ? 1 : 0.06213712;
		const newScoreRows: AttributeDataGridRow[] = [];
		if (counts) {
			for (let i = 0; i < scoreRows.length; i++) {
				if (scoreRows[i].grouped) {
					let groupCount = 0;
					let groupCountPercent = 0;
					let groupLength = 0;
					let groupLengthPercent = 0;
					const conversion = isMetric ? 1 : 0.06213712;

					const newGroup = scoreRows[i].grouped?.map(group => {
						const count = counts.find(
							count => count.key === group.value,
						);
						if (count !== undefined) {
							groupCount += count.count;
							groupCountPercent += count.count_percent;
							groupLength += count.length;
							groupLengthPercent += count.length_percent;

							return {
								...group,
								count: count.count,
								length:
									Math.round(count.length * conversion) / 100,
								countPercent:
									Math.round(count.count_percent * 1000) / 10,
								lengthPercent:
									Math.round(count.length_percent * 1000) /
									10,
							};
						} else {
							return group;
						}
					});

					groupCountPercent =
						Math.round(groupCountPercent * 1000) / 100;
					groupLength = Math.round(length * conversion) / 100;
					groupLengthPercent =
						Math.round(groupLengthPercent * 1000) / 10;

					newScoreRows.push({
						...scoreRows[i],
						count: groupCount,
						length: Math.round(groupLength * conversion) / 100,
						countPercent: Math.round(groupCountPercent * 1000) / 10,
						lengthPercent:
							Math.round(groupLengthPercent * 1000) / 10,
						grouped: newGroup,
					});
				} else {
					const count = counts.find(
						({ key }) =>
							(key === undefined || key === null
								? '--'
								: key.toString()) ===
							scoreRows[i].value.toString(),
					);

					if (count) {
						newScoreRows.push({
							...scoreRows[i],
							count: count?.count,
							length: Math.round(count.length * conversion) / 100,
							countPercent:
								Math.round(count.count_percent * 1000) / 10,
							lengthPercent:
								Math.round(count.length_percent * 1000) / 10,
						});
					} else {
						newScoreRows.push({
							...scoreRows[i],
							count: 0,
							length: 0,
							countPercent: 0,
							lengthPercent: 0,
						});
					}
				}
			}
		} else {
			for (let i = 0; i < scoreRows.length; i++) {
				newScoreRows.push({
					...scoreRows[i],
					count: 0,
					length: 0,
					countPercent: 0,
					lengthPercent: 0,
				});
			}
		}

		return newScoreRows;
	}
};

interface UniqueDataGridProps {
	countKey: string;
	option: AttributeOption;
	isMetric: boolean;
	onUniqueValuesChanged: () => void;
}

const UniqueDataGrid = ({
	countKey,
	option,
	isMetric,
	onUniqueValuesChanged,
}: UniqueDataGridProps) => {
	const dispatch = useDispatch();

	const [rowSelectionModel, setRowSelectionModel] =
		React.useState<GridRowSelectionModel>([]);

	const selectedStatus = selectSelectedStatus();
	const selectedDbIds = selectUniqueSelectedIds();
	const rows = selectAttributeDataGrid();

	const isCountLoading = selectIsCountLoading(countKey);

	const onGroupActions = (group: boolean, id?: number) => {
		const newScoreRows: AttributeDataGridRow[] = [];
		if (group) {
			// Find all the selected rows and remove them from the table and add them to a grouped row
			let count: number | undefined;
			let countPercent: number | undefined;
			let length: number | undefined;
			let lengthPercent: number | undefined;
			const groupName: string[] = [];
			const group: AttributeDataGridRow[] = [];

			rows.forEach(row => {
				if (rowSelectionModel.find(id => id === row.id) !== undefined) {
					if (row.count) count = (count ?? 0) + row.count;
					if (row.countPercent)
						countPercent = (countPercent ?? 0) + row.countPercent;
					if (row.length) length = (length ?? 0) + row.length;
					if (row.lengthPercent)
						lengthPercent =
							(lengthPercent ?? 0) + row.lengthPercent;

					groupName.push(row.value);
					group.push(row);
				} else {
					newScoreRows.push(row);
				}
			});

			if (countPercent)
				countPercent = Math.round(countPercent * 1000) / 100;

			if (length) {
				const conversion = isMetric ? 1 : 0.06213712;
				length = Math.round(length * conversion) / 100;
			}

			if (lengthPercent)
				lengthPercent = Math.round(lengthPercent * 1000) / 10;

			const groupedRow: AttributeDataGridRow = {
				id: group[0].id,
				value: groupName.join(', '),
				count: count,
				countPercent: countPercent,
				length: length,
				lengthPercent: lengthPercent,
				grouped: group,
			};

			newScoreRows.push(groupedRow);
		} else {
			if (id !== undefined) {
				rows.forEach(row => {
					if (id === row.id) {
						if (row.grouped) {
							newScoreRows.push(...row.grouped);
						}
					} else {
						newScoreRows.push(row);
					}
				});
			} else {
				// Find the row and ungroup it
				rows.forEach(row => {
					if (
						rowSelectionModel.find(id => id === row.id) !==
						undefined
					) {
						if (row.grouped) {
							newScoreRows.push(...row.grouped);
						}
					} else {
						newScoreRows.push(row);
					}
				});
			}
		}

		dispatch(setUniqueSelectedStatus(''));
		dispatch(setUniqueSelectedIds([]));
		dispatch(
			setAttributeDataGrid(newScoreRows.sort((a, b) => a.id - b.id)),
		);
		onUniqueValuesChanged();
	};

	const onExcludeActions = (exclude: boolean, id?: number) => {
		// Find and update the selected items
		if (id !== undefined) {
			const newScoreRows = rows.map(row => {
				if (id === row.id) {
					return { ...row, excluded: exclude };
				} else {
					return row;
				}
			});
			dispatch(setAttributeDataGrid(newScoreRows));
		} else {
			const newScoreRows = rows.map(row => {
				if (rowSelectionModel.find(id => id === row.id) !== undefined) {
					return { ...row, excluded: exclude };
				} else {
					return row;
				}
			});
			dispatch(setAttributeDataGrid(newScoreRows));
		}
		dispatch(setUniqueSelectedStatus(''));
		dispatch(setUniqueSelectedIds([]));
		onUniqueValuesChanged();
	};

	const handleRowSelectionModelChange = useCallback(
		(newRowSelectionModel: GridRowSelectionModel) => {
			// Clear or find the status before isRowSelectable is called
			// It's done in the effect above but this is not until AFTER isRowSelectable is called an so it get's out of step
			let status = '';
			if (newRowSelectionModel.length === 1) {
				const id = newRowSelectionModel[0];
				const selectedItem = rows.find(
					(row: AttributeDataGridRow) => id === row.id,
				);

				if (selectedItem) {
					status += selectedItem.excluded ? 'e' : 'i';
					if (selectedItem.grouped !== undefined) status += 'u';
				}
			} else if (newRowSelectionModel.length > 1) {
				const id = newRowSelectionModel[0];
				const selectedItem = rows.find(
					(row: AttributeDataGridRow) => id === row.id,
				);

				if (selectedItem) {
					status += selectedItem.excluded ? 'e' : 'i';
					if (selectedItem.grouped === undefined) status += 'g';
				}
			}

			dispatch(setUniqueSelectedStatus(status));

			setRowSelectionModel(newRowSelectionModel);
			dispatch(
				setUniqueSelectedIds(
					newRowSelectionModel.map((value: GridRowId) => {
						return value.toString();
					}),
				),
			);
		},
		[rows, rowSelectionModel],
	);

	const isRowSelectable = React.useCallback(
		(params: GridRowParams) => {
			let result = false;
			if (!isCountLoading && params.row['value'] !== '--') {
				if (selectedStatus.length === 0) {
					result = true;
				} else {
					if (selectedStatus.includes('e'))
						result = params.row.excluded;
					else if (selectedStatus.includes('i'))
						result = !params.row.excluded;

					if (selectedStatus.includes('g'))
						result = result && params.row.grouped === undefined;
					else if (selectedStatus.includes('u'))
						result = result && params.row.grouped !== undefined;
				}
			}
			return result;
		},
		[selectedStatus],
	);

	useEffect(() => {
		if (selectedDbIds.length === 0) setRowSelectionModel([]);
	}, [selectedDbIds]);

	return (
		<DataGrid
			disableColumnMenu
			disableColumnReorder
			columns={tableColumns(countKey, onExcludeActions, onGroupActions)}
			rows={rows}
			checkboxSelection={true}
			sx={{
				'& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer':
					{
						display: 'none',
					},
			}}
			isRowSelectable={(params: GridRowParams) => isRowSelectable(params)}
			onRowSelectionModelChange={handleRowSelectionModelChange}
			rowSelectionModel={rowSelectionModel}
			slots={{
				toolbar: TableToolbar,
			}}
			slotProps={{
				toolbar: {
					name: option.value,
					isLoading: isCountLoading,
					actionEnabled: true,
					selectedStatus: selectedStatus,
					onGroupActions: onGroupActions,
					onExcludeActions: onExcludeActions,
				},
			}}
			dataCy="score-table"
		/>
	);
};

export default UniqueDataGrid;
